import { Component, AfterContentInit, Input, SimpleChanges } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { BreadcrumbItem } from "../types/breadcrumb.types";

@Component({
  selector: "ds-breadcrumb",
  templateUrl: "./ds-breadcrumb.component.html",
  styleUrls: ["./ds-breadcrumb.component.scss"],
})
export class DsBreadcrumbComponent implements AfterContentInit {
  @Input() startFrom?: string;
  @Input() customLabels?: { [key: string]: string };
  @Input() customBreadcrumbs?: BreadcrumbItem[];

  currentPath = "";
  currentParams: any = {};
  generatedBreadcrumbs: BreadcrumbItem[] = [];
  isHovered = false;
  constructor(private router: Router, private route: ActivatedRoute) {}

  ngAfterContentInit() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.updateCurrentRoute());
    this.updateCurrentRoute();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["startFrom"] || changes["customLabels"] || changes["customBreadcrumbs"]) {
      this.updateCurrentRoute();
    }
  }

  private updateCurrentRoute() {
    const tree = this.router.parseUrl(this.router.url);
    this.currentPath = "/" + (tree.root.children["primary"]?.segments.map((s) => s.path).join("/") || "");
    this.currentParams = this.route.snapshot.queryParams;
    this.generateBreadcrumbs();
  }

  private generateBreadcrumbs() {
    this.generatedBreadcrumbs = [];
    if (this.customBreadcrumbs && this.customBreadcrumbs.length > 0) {
      this.buildFullBreadcrumbPath(this.customBreadcrumbs, "", []);
    } else {
      const breadcrumbConfigs: BreadcrumbItem[] = this.getBreadcrumbConfig();

      breadcrumbConfigs.forEach((config) => {
        const item: BreadcrumbItem = {
          label: config.label,
          url: config.url,
          queryParams: config.queryParams || {},
          isActive: this.isItemActive(config.url, config.queryParams || {}),
        };
        this.generatedBreadcrumbs.push(item);
      });
    }
  }

  private buildFullBreadcrumbPath(
    items: BreadcrumbItem[],
    currentPath: string,
    breadcrumbPath: BreadcrumbItem[]
  ): boolean {
    for (const item of items) {
      let fullPath: string;
      if (item.url.startsWith("/")) {
        fullPath = item.url;
      } else {
        fullPath = currentPath + item.url;
      }
      const queryParams = item.queryParams || {};
      const breadcrumbItem: BreadcrumbItem = {
        label: item.label,
        url: fullPath,
        queryParams: queryParams,
        isActive: this.isItemActive(fullPath, queryParams),
      };
      const newBreadcrumbPath = [...breadcrumbPath, breadcrumbItem];
      if (this.isCurrentPath(fullPath, queryParams)) {
        this.generatedBreadcrumbs = newBreadcrumbPath;
        return true;
      }
      if (item.children && item.children.length > 0) {
        if (this.buildFullBreadcrumbPath(item.children, fullPath, newBreadcrumbPath)) {
          return true;
        }
      }
      if (!item.children || item.children.length === 0) {
        if (
          this.currentPath === fullPath &&
          Object.keys(this.currentParams).length === 0 &&
          Object.keys(queryParams).length === 0
        ) {
          this.generatedBreadcrumbs = newBreadcrumbPath;
          return true;
        }
      }
    }
    return false;
  }

  private isCurrentPath(itemPath: string, itemQueryParams: any): boolean {
    const pathMatches = this.currentPath === itemPath;
    if (!itemQueryParams || Object.keys(itemQueryParams).length === 0) {
      return pathMatches;
    }
    const paramsMatch = this.compareParams(this.currentParams, itemQueryParams);
    return pathMatches && paramsMatch;
  }

  private getBreadcrumbConfig(): BreadcrumbItem[] {
    const segments = this.currentPath.split("/").filter((segment) => segment);
    const configs: BreadcrumbItem[] = [];

    if (this.startFrom) {
      const startSegments = this.startFrom.split("/").filter((segment) => segment);
      let startPath = "";

      startSegments.forEach((segment) => {
        startPath += `/${segment}`;
        const label = this.convertSegmentToLabel(segment);
        configs.push({ url: startPath, label: label });
      });

      const remainingSegments = segments.slice(startSegments.length);
      remainingSegments.forEach((segment, index) => {
        startPath += `/${segment}`;
        const label = this.convertSegmentToLabel(segment);

        let queryParams = {};
        if (index === remainingSegments.length - 1 && Object.keys(this.currentParams).length > 0) {
          queryParams = { ...this.currentParams };
        }

        configs.push({
          url: startPath,
          label: label,
          queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        });
      });
    } else {
      const homeLabel = this.customLabels && this.customLabels["home"] ? this.customLabels["home"] : "Home";
      configs.push({ url: "/", label: homeLabel });
      let currentPath = "";
      segments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const label = this.convertSegmentToLabel(segment);
        let queryParams = {};
        if (index === segments.length - 1 && Object.keys(this.currentParams).length > 0) {
          queryParams = { ...this.currentParams };
        }

        configs.push({
          url: currentPath,
          label: label,
          queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        });
      });
    }

    return configs;
  }

  private convertSegmentToLabel(segment: string): string {
    if (this.customLabels && this.customLabels[segment]) {
      return this.customLabels[segment];
    }

    if (/^\d+$/.test(segment)) {
      return segment;
    }
    return this.formatSegmentToLabel(segment);
  }

  private formatSegmentToLabel(segment: string): string {
    let formatted = segment.replace(/[-_]/g, " ");
    formatted = formatted
      .split(" ")
      .map((word) => this.capitalizeFirst(word))
      .join(" ");

    return formatted;
  }

  private capitalizeFirst(str: string): string {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  private isItemActive(path: string, queryParams: any): boolean {
    const samePath = this.currentPath === path;

    if (!queryParams || Object.keys(queryParams).length === 0) {
      return samePath && Object.keys(this.currentParams).length === 0;
    }

    const sameParams = this.compareParams(this.currentParams, queryParams);
    return samePath && sameParams;
  }

  private compareParams(a: any, b: any): boolean {
    const keysA = Object.keys(a || {});
    const keysB = Object.keys(b || {});
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => a[k] == b[k]);
  }
}
